import { MessageSquare, Mail, StickyNote, Sparkles } from 'lucide-react';
import { UnifiedTimelineCard } from '../../communication-timeline/slices/UnifiedTimelineCard.js';

/**
 * Page DOMAIN — Communications & Notes.
 *
 * Cette page est un ORCHESTRATEUR MINCE : aucune logique métier ici,
 * uniquement la composition des slices :
 *   · UnifiedTimelineCard (domaine communication-timeline)
 *   · KPIs résumés (à venir vague 2)
 *
 * Toute l'IO + agrégation est dans :
 *   - apps/api/src/services/communication-timeline/ (backend)
 *   - apps/web-v2/src/domains/communication-timeline/ (frontend hooks + slices)
 *   - apps/web-v2/src/domains/notes/                  (frontend notes slice)
 *
 * Convention slice respectée (Rafik 29/05/2026) :
 *   "toutes les fonctionnalités sont développées dans une page dédiée pour ne
 *    pas charger le code de la page" → ici la page se contente d'importer et
 *    composer des slices, rien de plus.
 */
export function CommunicationsPage() {
  return (
    <div className="mx-auto max-w-7xl space-y-5">
      {/* Header */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
          Domaine — Centralisation
        </div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-neutral-900">
          <MessageSquare className="text-primary-600 h-6 w-6" />
          Communications &amp; Notes
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          Source de vérité pour tous les échanges avec les apprenants — emails sortants, notes
          internes, RDV. La fiche apprenant en tire un slice filtré.
        </p>
      </div>

      {/* Bandeau vague 1 — info honnête */}
      <div className="flex items-start gap-3 rounded-xl border border-sky-200 bg-sky-50 p-3">
        <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-600" />
        <div className="flex-1 text-[11px] text-sky-900">
          <span className="font-semibold">Vague 1 livrée :</span> backend email_log + notes
          branchés, UI timeline + ajout note opérationnels. Les emails sortants apparaîtront ici dès
          la vague 2 (instrumentation des senders : Resend, Moodle welcome, Qualiopi). Les notes que
          tu ajoutes maintenant sont déjà persistées en base.
        </div>
      </div>

      {/* KPI mini-bandeau */}
      <div className="grid gap-3 md:grid-cols-3">
        <KpiCard
          icon={<Mail className="h-4 w-4 text-sky-600" />}
          label="Emails loggés"
          value="–"
          subtitle="Vague 2"
        />
        <KpiCard
          icon={<StickyNote className="h-4 w-4 text-amber-600" />}
          label="Notes équipe"
          value="–"
          subtitle="Sync timeline"
        />
        <KpiCard
          icon={<MessageSquare className="h-4 w-4 text-emerald-600" />}
          label="RDV à venir"
          value="–"
          subtitle="Vague 3"
        />
      </div>

      {/* Timeline globale unifiée — composition pure */}
      <UnifiedTimelineCard
        title="Timeline globale — toutes les communications"
        showApprenantLinks
      />

      {/* Hint */}
      <div className="rounded-xl border border-neutral-200 bg-white p-4 text-[11px] text-neutral-600">
        <span className="font-semibold">💡 Astuce :</span> pour ajouter une note à un apprenant
        précis, ouvre sa fiche depuis la nav <strong>Apprenants</strong>. Le slice "Notes internes"
        de la fiche permet la saisie ; les notes apparaîtront ici dans la timeline globale en temps
        réel.
      </div>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  subtitle,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtitle: string;
}) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-3">
      <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold text-neutral-900">{value}</div>
      <div className="text-[10px] text-neutral-500">{subtitle}</div>
    </div>
  );
}
